/* eslint-disable no-underscore-dangle */
import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Spinner, Button, Form,
} from 'react-bootstrap';
import PaymentsService from '../../../Services/PaymentsService';

// Inline money formatter — mirrors the other Payments components (no shared
// util has been promoted; consolidating would touch too many files for this).
function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  const symbol = currency === 'EUR' ? '€' : `${currency || ''} `;
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ymd(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

const REASON_LABEL = {
  email: 'email ✓',
  amount: 'amount ✓',
  'amount≈': '~amount',
  date: 'date ✓',
  name: 'name ✓',
  'name~': '~name',
};

// Ranked booking suggestions for a payment being reconciled.
// `signals` = { amount, currency, date, email, name }. `name` is the payer name
// off the Stripe billing details; it re-ranks candidates but never widens the
// set, so it is NOT part of `canQuery` — a name on its own would make the API
// return [] and the panel flash empty for nothing.
// `onPick(booking)` attaches one;
// `onPickMany(bookings)` attaches several (checkbox selection). `excludeIds`
// hides bookings already chosen. Renders nothing when there are no usable
// signals or no matches, so it stays out of the way.
function BookingSuggestions({
  signals = {}, onPick, onPickMany = null, excludeIds = [],
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const seqRef = useRef(0);

  const {
    amount, currency, date, email, name,
  } = signals;
  const canQuery = (Number(amount) > 0 && !!currency) || !!email;

  useEffect(() => {
    if (!canQuery) {
      setItems([]);
      return undefined;
    }
    seqRef.current += 1;
    const seq = seqRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await PaymentsService.suggestBookings({
          amount, currency, date, email, name,
        });
        if (seqRef.current === seq) setItems(Array.isArray(res) ? res : []);
      } catch {
        if (seqRef.current === seq) setItems([]);
      } finally {
        if (seqRef.current === seq) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [amount, currency, date, email, name, canQuery]);

  const visible = items.filter((b) => !excludeIds.includes(b.booking_id));

  // Drop any selections that are no longer visible (attached / re-queried).
  const visibleIds = visible.map((b) => b.booking_id);
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));

  if (!canQuery) return null;
  if (!loading && visible.length === 0) return null;

  const toggle = (id) => setSelected((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  const addSelected = () => {
    const picked = visible.filter((b) => selectedVisible.includes(b.booking_id));
    if (onPickMany) onPickMany(picked);
    else picked.forEach((b) => onPick(b));
    setSelected([]);
  };

  return (
    <div className="mb-3 border rounded p-2 bg-light">
      <div className="d-flex align-items-center gap-2 mb-2">
        <i className="fa fa-wand-magic-sparkles text-primary" />
        <strong className="small">Suggested matches</strong>
        {loading && <Spinner size="sm" animation="border" className="ms-1" />}
        {selectedVisible.length > 0 && (
          <Button size="sm" variant="primary" className="ms-auto py-0" onClick={addSelected}>
            {`Add ${selectedVisible.length} selected`}
          </Button>
        )}
      </div>
      <div className="d-flex flex-column gap-1">
        {visible.map((b) => (
          <div
            key={b.booking_id}
            className="d-flex align-items-center gap-2 border rounded px-2 py-1 bg-white"
          >
            <Form.Check
              type="checkbox"
              checked={selectedVisible.includes(b.booking_id)}
              onChange={() => toggle(b.booking_id)}
              aria-label={`Select ${b.booking_reference || b.booking_id}`}
            />
            <button
              type="button"
              className="btn btn-link btn-sm text-start text-decoration-none text-body flex-grow-1 d-flex justify-content-between align-items-center gap-2 p-0"
              onClick={() => onPick(b)}
              title="Attach this booking"
            >
              <span className="text-truncate">
                <strong>{b.booking_reference || b.booking_id}</strong>
                {b.full_name ? ` · ${b.full_name}` : ''}
                {b.email_address ? (
                  <span className="text-muted">{` · ${b.email_address}`}</span>
                ) : null}
                {b.date_datetime ? (
                  <span className="text-muted">{` · ${ymd(b.date_datetime)}`}</span>
                ) : null}
              </span>
              <span className="text-nowrap">
                {(b.reasons || []).map((r) => (
                  <Badge bg="info" text="dark" className="me-1" key={r}>
                    {REASON_LABEL[r] || r}
                  </Badge>
                ))}
                <strong>{formatMoney(b.price, b.currency)}</strong>
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BookingSuggestions;
